// Magaza vitrini (Noel serisi 4. bolum): shop title, duyuru, banner metni,
// hikaye ve SSS'yi bir kerede uretir. Etsy API'si yalniz shop title ve
// duyuruyu yazabiliyor; hikaye, SSS ve banner Etsy panelinde elle yapistirilir.

import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Store, Loader2, Sparkles, Copy, Check, Upload, Info } from "lucide-react";
import { authHeaders, getBackendUrl } from "../lib/http";

type Storefront = {
  shopTitle: string; announcement: string; bannerText: string;
  storyHeadline: string; story: string; faq: Array<{ q: string; a: string }>;
};

async function post<T>(path: string, body: unknown): Promise<T> {
  const r = await fetch(`${getBackendUrl()}/api/storefront/${path}`, { method: "POST", headers: authHeaders(), body: JSON.stringify(body) });
  const b = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(b.error || `Hata (${r.status})`);
  return b.data as T;
}

function CopyBtn({ text }: { text: string }) {
  const [ok, setOk] = useState(false);
  return (
    <button
      onClick={() => { void navigator.clipboard.writeText(text); setOk(true); setTimeout(() => setOk(false), 1200); }}
      className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700"
    >
      {ok ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}{ok ? "Kopyalandı" : "Kopyala"}
    </button>
  );
}

type Plan = {
  sections: Array<{ id: string; title: string; count: number | null }>;
  suggested: Array<{ title: string; sectionId: string | null; matchCount: number }>;
  listings: Array<{ listingId: string; title: string; currentSectionId: string | null; proposed: string | null; proposedSectionId: string | null }>;
};

/** Section plani (Hakan Bey: 20 section'in hepsini ac): ornek magaza duzeniyle
 *  eksikleri olustur, yayindaki listeleri basligina gore section'a ata. */
function SectionsCard() {
  const [onlyEmpty, setOnlyEmpty] = useState(true);
  const [pick, setPick] = useState<Record<string, boolean>>({});
  const plan = useMutation({
    mutationFn: async () => {
      const r = await fetch(`${getBackendUrl()}/api/storefront/sections/plan`, { headers: authHeaders() });
      const b = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(b.error || `Hata (${r.status})`);
      return b.data as Plan;
    },
    onSuccess: (d) => setPick(Object.fromEntries(d.suggested.filter((x) => !x.sectionId).map((x) => [x.title, x.matchCount > 0]))),
  });
  const create = useMutation({
    mutationFn: (titles: string[]) => post<{ created: string[] }>("sections/create", { titles }),
    onSuccess: () => plan.mutate(),
  });
  const assign = useMutation({
    mutationFn: (items: Array<{ listingId: string; sectionId: string }>) => post<{ ok: number; errors: string[] }>("sections/assign", { items }),
    onSuccess: () => plan.mutate(),
  });
  const d = plan.data;
  const moves = (d?.listings ?? []).filter((x) => x.proposedSectionId && x.proposedSectionId !== x.currentSectionId && (!onlyEmpty || !x.currentSectionId));
  const missing = (d?.suggested ?? []).filter((x) => !x.sectionId);
  const sectionTitle = (id: string | null) => d?.sections.find((x) => x.id === id)?.title ?? "—";
  const extra = (d?.sections ?? []).filter((x) => !d?.suggested.some((g) => g.sectionId === x.id));

  return (
    <div className="space-y-3 rounded-xl border border-gray-200 bg-white p-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">Section'lar</h2>
          <p className="text-xs text-gray-500">Örnek mağaza düzeni (takı / tişört / hediyelik). Etsy zengin section'ı sever; alıcı mağazada kaybolmaz. Yeni yayınlanan liste de başlığına göre section'a konur.</p>
        </div>
        <button onClick={() => plan.mutate()} disabled={plan.isPending}
          className="flex shrink-0 items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
          {plan.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {d ? "Yenile" : "Planı getir"}
        </button>
      </div>
      {plan.isError && <p className="text-sm text-red-600">{(plan.error as Error).message}</p>}
      {d && d.suggested.length === 0 && <p className="text-sm text-gray-500">Bu mağaza türü için hazır section düzeni yok (şimdilik takı, tişört ve hediyelik).</p>}
      {d && d.suggested.length > 0 && (
        <>
          <div className="divide-y divide-gray-100 rounded-lg border border-gray-200">
            {d.suggested.map((g) => (
              <label key={g.title} className="flex items-center gap-2 px-3 py-1.5 text-sm">
                {g.sectionId
                  ? <Check className="h-4 w-4 shrink-0 text-emerald-600" />
                  : <input type="checkbox" checked={!!pick[g.title]} onChange={(e) => setPick({ ...pick, [g.title]: e.target.checked })} />}
                <span className="flex-1 text-gray-800">{g.title}</span>
                <span className="text-xs text-gray-500">{g.matchCount} liste uyuyor</span>
                <span className={`text-xs ${g.sectionId ? "text-emerald-700" : "text-amber-700"}`}>{g.sectionId ? "var" : "yok"}</span>
              </label>
            ))}
          </div>
          {extra.length > 0 && <p className="text-xs text-gray-500">Planda olmayan mevcut section'lar: {extra.map((x) => x.title).join(", ")}</p>}
          {missing.length > 0 && (
            <button onClick={() => create.mutate(missing.filter((x) => pick[x.title]).map((x) => x.title))} disabled={create.isPending || !missing.some((x) => pick[x.title])}
              className="rounded-lg border border-indigo-300 px-3 py-1.5 text-sm font-medium text-indigo-700 hover:bg-indigo-50 disabled:opacity-50">
              {create.isPending ? "Oluşturuluyor…" : `Seçili ${missing.filter((x) => pick[x.title]).length} section'ı oluştur`}
            </button>
          )}
          {create.isError && <p className="text-sm text-red-600">{(create.error as Error).message}</p>}

          <div className="border-t border-gray-100 pt-3">
            <div className="mb-2 flex items-center justify-between gap-3">
              <p className="text-sm font-medium text-gray-800">Atanacak listeler ({moves.length})</p>
              <label className="flex items-center gap-1.5 text-xs text-gray-600">
                <input type="checkbox" checked={onlyEmpty} onChange={(e) => setOnlyEmpty(e.target.checked)} /> Yalnız section'ı boş olanlar
              </label>
            </div>
            {moves.length > 0 ? (
              <>
                <div className="max-h-72 overflow-y-auto rounded-lg border border-gray-200">
                  {moves.slice(0, 200).map((m) => (
                    <div key={m.listingId} className="flex items-center gap-2 border-b border-gray-100 px-3 py-1 text-xs last:border-0">
                      <span className="min-w-0 flex-1 truncate text-gray-700" title={m.title}>{m.title}</span>
                      <span className="shrink-0 text-gray-400">{sectionTitle(m.currentSectionId)} →</span>
                      <span className="shrink-0 font-medium text-indigo-700">{m.proposed}</span>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => window.confirm(`${moves.length} liste Etsy'de önerilen section'a taşınsın mı?`) && assign.mutate(moves.map((m) => ({ listingId: m.listingId, sectionId: m.proposedSectionId! })))}
                  disabled={assign.isPending}
                  className="mt-2 flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
                  {assign.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {moves.length} listeyi ata
                </button>
              </>
            ) : <p className="text-xs text-gray-500">Atanacak liste yok (önerilen section önce oluşturulmalı ya da listeler zaten yerinde).</p>}
            {assign.isSuccess && <p className="mt-1 text-sm text-emerald-700">{assign.data.ok} liste atandı{assign.data.errors.length ? `, ${assign.data.errors.length} hata` : ""}.</p>}
            {assign.isError && <p className="mt-1 text-sm text-red-600">{(assign.error as Error).message}</p>}
          </div>
        </>
      )}
    </div>
  );
}

export default function StorefrontPage() {
  const [input, setInput] = useState({ products: "", discount: "", storyFacts: "", processingDays: "3-5 business days", shippingDays: "" });
  const [data, setData] = useState<Storefront | null>(null);
  const gen = useMutation({ mutationFn: () => post<Storefront>("generate", input), onSuccess: (d) => setData(d) });
  const apply = useMutation({ mutationFn: (d: Storefront) => post("apply", { shopTitle: d.shopTitle, announcement: d.announcement }) });

  const field = "w-full rounded-lg border border-gray-200 px-3 py-2 text-sm";
  const set = (k: keyof typeof input) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setInput({ ...input, [k]: e.target.value });
  const edit = (k: keyof Omit<Storefront, "faq">) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => data && setData({ ...data, [k]: e.target.value });

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900"><Store className="h-6 w-6 text-indigo-600" /> Mağaza Vitrini</h1>
        <p className="mt-1 text-sm text-gray-500">Shop title, duyuru, banner metni, hikâye ve sık sorulan sorular — İngilizce, emojisiz</p>
      </div>

      <div className="space-y-3 rounded-xl border border-gray-200 bg-white p-5">
        <label className="block text-sm text-gray-700">Ne satıyorsun?
          <input value={input.products} onChange={set("products")} placeholder="ör. 925 sterling silver ve gold plated kişiselleştirilmiş takılar" className={`mt-1 ${field}`} />
        </label>
        <div className="grid gap-3 sm:grid-cols-3">
          <label className="block text-sm text-gray-700">İndirim (varsa)
            <input value={input.discount} onChange={set("discount")} placeholder="ör. 40% off store-wide" className={`mt-1 ${field}`} />
          </label>
          <label className="block text-sm text-gray-700">Üretim süresi
            <input value={input.processingDays} onChange={set("processingDays")} className={`mt-1 ${field}`} />
          </label>
          <label className="block text-sm text-gray-700">Kargo süresi
            <input value={input.shippingDays} onChange={set("shippingDays")} placeholder="ör. US 3-7, Europe 5-10 business days" className={`mt-1 ${field}`} />
          </label>
        </div>
        <label className="block text-sm text-gray-700">Hikâye için kendi bilgilerin (Türkçe olabilir)
          <textarea value={input.storyFacts} onChange={set("storyFacts")} rows={3} placeholder="ör. takıya nasıl başladın, ne önemsiyorsun, hedeflerin — boş bırakırsan genel bir hikâye yazılır" className={`mt-1 ${field}`} />
        </label>
        {gen.isError && <p className="text-sm text-red-600">{(gen.error as Error).message}</p>}
        <button onClick={() => gen.mutate()} disabled={gen.isPending}
          className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
          {gen.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Metinleri üret
        </button>
      </div>

      <SectionsCard />

      {data && (
        <>
          <div className="space-y-3 rounded-xl border border-gray-200 bg-white p-5">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-900">Etsy'ye doğrudan yazılabilenler</h2>
              <button onClick={() => window.confirm("Shop title ve duyuru Etsy mağazana yazılsın mı? Mevcut metinlerin yerine geçer.") && apply.mutate(data)} disabled={apply.isPending}
                className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
                {apply.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Etsy'ye yaz
              </button>
            </div>
            {apply.isSuccess && <p className="text-sm text-emerald-700">Shop title ve duyuru Etsy'ye yazıldı.</p>}
            {apply.isError && <p className="text-sm text-red-600">{(apply.error as Error).message}</p>}
            <label className="block text-sm text-gray-700">Shop title <span className="text-xs text-gray-400">({data.shopTitle.length}/55)</span>
              <input value={data.shopTitle} onChange={edit("shopTitle")} maxLength={55} className={`mt-1 ${field}`} />
            </label>
            <label className="block text-sm text-gray-700">Duyuru (announcement)
              <textarea value={data.announcement} onChange={edit("announcement")} rows={8} className={`mt-1 ${field}`} />
            </label>
          </div>

          <div className="space-y-4 rounded-xl border border-gray-200 bg-white p-5">
            <div className="flex gap-2 rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-600">
              <Info className="mt-0.5 h-4 w-4 shrink-0" /> Etsy API'si bunlara izin vermiyor: Shop Manager → Settings → Info &amp; Appearance (hikâye, banner) ve About/FAQ bölümüne kopyalayıp yapıştır. En az 3 shop member eklemeyi unutma.
            </div>
            <div>
              <div className="mb-1 flex items-center justify-between"><p className="text-sm font-medium text-gray-800">Banner metni</p><CopyBtn text={data.bannerText} /></div>
              <input value={data.bannerText} onChange={edit("bannerText")} className={field} />
            </div>
            <div>
              <div className="mb-1 flex items-center justify-between"><p className="text-sm font-medium text-gray-800">Hikâye başlığı + hikâye</p><CopyBtn text={`${data.storyHeadline}\n\n${data.story}`} /></div>
              <input value={data.storyHeadline} onChange={edit("storyHeadline")} className={`mb-2 ${field}`} />
              <textarea value={data.story} onChange={edit("story")} rows={8} className={field} />
            </div>
            <div>
              <div className="mb-1 flex items-center justify-between"><p className="text-sm font-medium text-gray-800">Sık sorulan sorular ({data.faq.length})</p><CopyBtn text={data.faq.map((f) => `${f.q}\n${f.a}`).join("\n\n")} /></div>
              <div className="divide-y divide-gray-100 rounded-lg border border-gray-200">
                {data.faq.map((f, i) => (
                  <div key={i} className="px-3 py-2">
                    <div className="flex items-start justify-between gap-3">
                      <p className="text-sm font-medium text-gray-800">{f.q}</p>
                      <CopyBtn text={`${f.q}\n${f.a}`} />
                    </div>
                    <p className="mt-0.5 text-xs text-gray-600">{f.a}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
